import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Building2,
  User,
  Search,
  Edit,
  ToggleLeft,
  ToggleRight,
  CheckCircle2,
  Clock4,
  XCircle,
  Mail,
  Phone,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/lib/useDebounce'
import {
  useGetVendorsQuery,
  useGetVendorSetupListQuery,
  useUpdateVendorMutation,
  type Vendor,
} from '@/redux/service/vendor'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import EditVendorModal from '@/components/EditVendorModal'
import { CustomPagination } from '@/components/CustomPagination'
import toast from 'react-hot-toast'

type FilterTab = 'all' | 'active' | 'pending' | 'inactive'

const TAB_LABELS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending Setup' },
  { key: 'inactive', label: 'Inactive' },
]

export default function Vendors() {
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<FilterTab>('all')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [createOpen, setCreateOpen] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const debouncedSearch = useDebounce(search, 300)
  const { hasPageAccess } = useCheckPermission()
  const isFinance = hasPageAccess('finance') || hasPageAccess('mini_finance')

  const [updateVendor, { isLoading: isToggling }] = useUpdateVendorMutation()

  const isPendingTab = tab === 'pending'

  const tabFilterParams =
    tab === 'active'
      ? { is_active: true as const }
      : tab === 'inactive'
        ? { is_active: false as const }
        : {}

  /* The "Pending Setup" tab has its own dedicated endpoint (oldest-first,
     matching the setup queue) instead of filtering the general list. */
  const {
    data: vendorListData,
    isLoading: isVendorListLoading,
    isFetching: isVendorListFetching,
    isError: isVendorListError,
  } = useGetVendorsQuery(
    {
      page,
      limit,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...tabFilterParams,
    },
    { skip: isPendingTab }
  )

  const {
    data: vendorSetupData,
    isLoading: isVendorSetupLoading,
    isFetching: isVendorSetupFetching,
    isError: isVendorSetupError,
  } = useGetVendorSetupListQuery(
    {
      page,
      limit,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    },
    { skip: !isPendingTab }
  )

  const vendorData = isPendingTab ? vendorSetupData : vendorListData
  const isLoading = isPendingTab ? isVendorSetupLoading : isVendorListLoading
  const isFetching = isPendingTab ? isVendorSetupFetching : isVendorListFetching
  const isError = isPendingTab ? isVendorSetupError : isVendorListError

  /* Stats always reflect the full vendor set, independent of the active
     tab/search filters, so the cards don't mutate when switching tabs. */
  const { data: totalStats } = useGetVendorsQuery({ limit: 1 })
  const { data: activeStats } = useGetVendorsQuery({
    limit: 1,
    is_active: true,
  })
  const { data: pendingStats } = useGetVendorSetupListQuery({ limit: 1 })
  const { data: inactiveStats } = useGetVendorsQuery({
    limit: 1,
    is_active: false,
  })

  const rows: Vendor[] = vendorData?.data ?? []
  const total = totalStats?.pagination.totalRecords ?? 0
  const activeCount = activeStats?.pagination.totalRecords ?? 0
  const pendingCount = pendingStats?.pagination.totalRecords ?? 0
  const inactiveCount = inactiveStats?.pagination.totalRecords ?? 0

  const handleTabChange = (next: FilterTab) => {
    setTab(next)
    setPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleToggleActive = async (vendor: Vendor) => {
    try {
      await updateVendor({
        id: vendor.vendor_id,
        is_active: !vendor.is_active,
      }).unwrap()
      toast.success(
        `${vendor.vendor_name} marked as ${!vendor.is_active ? 'active' : 'inactive'}`
      )
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to update vendor status')
    }
  }

  return (
    <div className='flex flex-col gap-6 h-full overflow-y-auto'>
      {/* ── Page header ── */}
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
        <div>
          <div className='flex items-center gap-2.5'>
            <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20'>
              <Building2 className='w-4 h-4 text-orange-500' />
            </div>
            <h1 className='text-xl font-bold text-foreground tracking-tight'>
              Vendor Management
            </h1>
          </div>
          <p className='text-xs text-muted-foreground mt-1 ml-10'>
            Vendors sync automatically from ClickUp - complete their setup here
            before using them on projects
          </p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        {[
          {
            label: 'Total Vendors',
            value: total,
            icon: Building2,
            color: 'text-orange-500',
            bg: 'bg-orange-500/10 border-orange-500/20',
          },
          {
            label: 'Active',
            value: activeCount,
            icon: CheckCircle2,
            color: 'text-green-500',
            bg: 'bg-green-500/10 border-green-500/20',
          },
          {
            label: 'Pending Setup',
            value: pendingCount,
            icon: Clock4,
            color: 'text-amber-500',
            bg: 'bg-amber-500/10 border-amber-500/20',
          },
          {
            label: 'Inactive',
            value: inactiveCount,
            icon: XCircle,
            color: 'text-muted-foreground',
            bg: 'bg-muted/40 border-border',
          },
        ].map(stat => (
          <div
            key={stat.label}
            className='flex items-center gap-3 p-4 rounded-xl border bg-card shadow-sm'
          >
            <div
              className={cn(
                'flex items-center justify-center w-9 h-9 rounded-lg border shrink-0',
                stat.bg
              )}
            >
              <stat.icon className={cn('w-4 h-4', stat.color)} />
            </div>
            <div>
              <p className='text-2xl font-bold text-foreground leading-none'>
                {isLoading ? (
                  <Skeleton className='h-6 w-8 rounded' />
                ) : (
                  stat.value
                )}
              </p>
              <p className='text-xs text-muted-foreground mt-0.5'>
                {stat.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Controls ── */}
      <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
        <div className='relative flex-1 max-w-sm p-1'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none' />
          <Input
            placeholder='Search vendors…'
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className='h-9 pl-9 text-sm'
          />
        </div>

        {/* Tabs */}
        <div className='flex items-center gap-1 p-1 rounded-lg bg-muted/40 border border-border'>
          {TAB_LABELS.map(t => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                tab === t.key
                  ? 'bg-background text-foreground shadow-sm border border-border'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t.label}
              {t.key === 'pending' && pendingCount > 0 && (
                <span className='ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold'>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className='border border-border rounded-lg bg-card shadow-sm overflow-hidden flex-1 min-h-0'>
        <div className='h-full overflow-x-auto'>
          <Table className='border-none'>
            <TableHeader>
              <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                  Vendor
                </TableHead>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                  Contact
                </TableHead>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                  Tech / Skills
                </TableHead>
                {isFinance && (
                  <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                    Rate ($/hr)
                  </TableHead>
                )}
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                  Status
                </TableHead>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4'>
                  Setup
                </TableHead>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right px-4'>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading || isFetching ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} className='border-b border-border/50'>
                    {[...Array(isFinance ? 7 : 6)].map((_, j) => (
                      <TableCell key={j} className='px-4 py-3'>
                        <Skeleton className='h-4 w-full rounded-md' />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={isFinance ? 7 : 6}
                    className='h-32 text-center'
                  >
                    <div className='flex flex-col items-center justify-center gap-1.5'>
                      <AlertCircle className='w-8 h-8 text-destructive/50' />
                      <p className='text-sm font-medium text-muted-foreground'>
                        Failed to load vendors
                      </p>
                      <p className='text-xs text-muted-foreground/60'>
                        You may not have access to this page, or something went
                        wrong. Try refreshing.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map(vendor => (
                  <TableRow
                    key={vendor.vendor_id}
                    className='border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 group'
                  >
                    {/* Vendor name + type */}
                    <TableCell className='px-4 py-3'>
                      <div className='flex items-center gap-2.5'>
                        <div
                          className={cn(
                            'flex items-center justify-center w-8 h-8 rounded-lg shrink-0 border',
                            vendor.vendor_type === 'company'
                              ? 'bg-orange-500/10 border-orange-500/20'
                              : 'bg-blue-500/10 border-blue-500/20'
                          )}
                        >
                          {vendor.vendor_type === 'company' ? (
                            <Building2
                              className={cn(
                                'w-4 h-4',
                                vendor.vendor_type === 'company'
                                  ? 'text-orange-500'
                                  : 'text-blue-500'
                              )}
                            />
                          ) : (
                            <User className='w-4 h-4 text-blue-500' />
                          )}
                        </div>
                        <div className='min-w-0'>
                          <p className='text-sm font-semibold text-foreground truncate'>
                            {vendor.vendor_name}
                          </p>
                          <p className='text-[10px] text-muted-foreground capitalize'>
                            {vendor.vendor_type === 'company'
                              ? (vendor.company_name ?? 'Company')
                              : (vendor.freelancer_name ?? 'Freelancer')}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    {/* Contact */}
                    <TableCell className='px-4 py-3'>
                      <p className='text-sm font-medium text-foreground'>
                        {vendor.contact_name ?? '—'}
                      </p>
                      <div className='flex flex-col gap-0.5 mt-0.5'>
                        {vendor.contact_email && (
                          <span className='text-[10px] text-muted-foreground flex items-center gap-1'>
                            <Mail className='w-2.5 h-2.5' />
                            {vendor.contact_email}
                          </span>
                        )}
                        {vendor.contact_phone && (
                          <span className='text-[10px] text-muted-foreground flex items-center gap-1'>
                            <Phone className='w-2.5 h-2.5' />
                            {vendor.contact_phone}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {/* Tech stack */}
                    <TableCell className='px-4 py-3'>
                      {vendor.tech_stack && vendor.tech_stack.length > 0 ? (
                        <div className='flex flex-wrap gap-1'>
                          {vendor.tech_stack.slice(0, 3).map(t => (
                            <span
                              key={t}
                              className='inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border'
                            >
                              {t}
                            </span>
                          ))}
                          {vendor.tech_stack.length > 3 && (
                            <span className='inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border'>
                              +{vendor.tech_stack.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className='text-muted-foreground text-sm'>—</span>
                      )}
                    </TableCell>

                    {/* Rate (finance only) */}
                    {isFinance && (
                      <TableCell className='px-4 py-3 text-sm font-semibold text-foreground'>
                        {vendor.default_rate != null
                          ? `$${Number(vendor.default_rate).toFixed(2)}`
                          : '—'}
                      </TableCell>
                    )}

                    {/* Active status */}
                    <TableCell className='px-4 py-3'>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border',
                          vendor.is_active
                            ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
                            : 'bg-muted text-muted-foreground border-border'
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            vendor.is_active
                              ? 'bg-green-500'
                              : 'bg-muted-foreground'
                          )}
                        />
                        {vendor.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>

                    {/* Setup status */}
                    <TableCell className='px-4 py-3'>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border',
                          vendor.setup_completed
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                        )}
                      >
                        {vendor.setup_completed ? (
                          <>
                            <CheckCircle2 className='w-2.5 h-2.5' />
                            Complete
                          </>
                        ) : (
                          <>
                            <Clock4 className='w-2.5 h-2.5' />
                            Pending
                          </>
                        )}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className='px-4 py-3'>
                      <div className='flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='h-7 w-7 hover:bg-accent'
                          onClick={() => {
                            setEditingVendor(vendor)
                            setCreateOpen(true)
                          }}
                          title='Edit vendor'
                        >
                          <Edit className='w-3.5 h-3.5' />
                        </Button>
                        <Button
                          size='icon'
                          variant='ghost'
                          className={cn(
                            'h-7 w-7',
                            vendor.is_active
                              ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                              : 'text-green-500 hover:bg-green-500/10'
                          )}
                          onClick={() => handleToggleActive(vendor)}
                          disabled={isToggling}
                          title={
                            vendor.is_active
                              ? 'Deactivate vendor'
                              : 'Activate vendor'
                          }
                        >
                          {vendor.is_active ? (
                            <ToggleRight className='w-4 h-4' />
                          ) : (
                            <ToggleLeft className='w-4 h-4' />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={isFinance ? 7 : 6}
                    className='h-32 text-center'
                  >
                    <div className='flex flex-col items-center justify-center gap-1.5'>
                      <Building2 className='w-8 h-8 text-muted-foreground/40' />
                      <p className='text-sm font-medium text-muted-foreground'>
                        No vendors found
                      </p>
                      <p className='text-xs text-muted-foreground/60'>
                        {search
                          ? 'Try a different search term'
                          : 'Vendors will appear here once synced from ClickUp'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {!isLoading && !isError && vendorData && vendorData.data.length > 0 && (
          <CustomPagination
            page={page}
            limit={limit}
            setPage={setPage}
            setLimit={setLimit}
            totalPages={vendorData.pagination.totalPages}
            totalRecords={vendorData.pagination.totalRecords}
            title='vendors'
          />
        )}
      </div>

      {/* ── Edit modal ── */}
      {editingVendor && (
        <EditVendorModal
          open={createOpen}
          onClose={() => {
            setCreateOpen(false)
            setEditingVendor(null)
          }}
          vendorToEdit={editingVendor}
        />
      )}
    </div>
  )
}
